import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, 
  TrendingUp, 
  TrendingDown, 
  Lock, 
  Loader2,
  Target,
  ShieldAlert,
  Crown,
  Clock,
  Zap,
  BarChart3
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SignalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, canViewSignal, isPremium, isAdmin } = useAuth();
  const [signal, setSignal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSignal();
  }, [id, token]);

  const fetchSignal = async () => {
    try {
      const response = await axios.get(`${API}/trading_setups/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSignal(response.data);
    } catch (error) {
      console.error('Failed to fetch signal:', error);
      toast.error('Failed to load signal');
      navigate('/signals');
    } finally {
      setLoading(false);
    }
  };

  // Helper to determine direction from bias field
  const getDirection = (sig) => {
    const bias = sig?.bias?.toLowerCase() || '';
    if (bias.includes('long') || bias.includes('bullish') || bias.includes('buy')) {
      return 'LONG';
    }
    if (bias.includes('short') || bias.includes('bearish') || bias.includes('sell')) {
      return 'SHORT';
    }
    return sig?.bias || 'N/A';
  };

  const isLongSignal = (sig) => {
    const direction = getDirection(sig);
    return direction === 'LONG' || direction.toLowerCase().includes('long') || direction.toLowerCase().includes('bullish');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#00FF94] animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!signal) {
    return (
      <Layout>
        <div className="text-center py-16">
          <h2 className="text-xl font-bold text-white mb-4">Signal not found</h2>
          <Button onClick={() => navigate('/signals')} variant="outline" className="border-[#1F1F1F]">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Signals
          </Button>
        </div>
      </Layout>
    );
  }

  const isLong = isLongSignal(signal);
  const direction = getDirection(signal);
  const signalIsPremium = Boolean(signal.Premium);
  const canView = canViewSignal(signal);

  // Paywall for non-premium users trying to view premium signal
  if (!canView) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto animate-fade-in-up">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/signals')}
            className="text-[#A1A1AA] hover:text-white mb-6"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Signals
          </Button>

          <Card className="border-[#1F1F1F] bg-[#0A0A0A] premium-border overflow-hidden">
            <CardContent className="p-0">
              <div className="p-8 text-center">
                {/* Lock Icon */}
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#6366F1]/10 flex items-center justify-center">
                  <Lock className="w-10 h-10 text-[#6366F1]" />
                </div>

                {/* Signal Preview */}
                <div className="flex items-center justify-center gap-2 mb-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    isLong ? 'bg-[#00FF94]/10' : 'bg-[#FF0055]/10'
                  }`}>
                    {isLong ? (
                      <TrendingUp className="w-5 h-5 text-[#00FF94]" />
                    ) : (
                      <TrendingDown className="w-5 h-5 text-[#FF0055]" />
                    )}
                  </div>
                  <h2 className="text-2xl font-bold text-white font-mono">{signal.symbol}</h2>
                  <Badge className="bg-[#6366F1]/10 text-[#6366F1] border border-[#6366F1]/20">
                    <Crown className="w-3 h-3 mr-1" />
                    Premium
                  </Badge>
                </div>

                <h3 className="text-xl font-bold text-white mb-2">This is a Premium Signal</h3>
                <p className="text-[#A1A1AA] mb-8 max-w-md mx-auto">
                  Upgrade to Premium to unlock this signal and get access to all premium trading setups with higher win rates.
                </p>

                {/* CTA */}
                <Button 
                  size="lg"
                  className="bg-[#6366F1] hover:bg-[#6366F1]/90 text-white font-bold"
                  onClick={() => toast.info('Premium upgrade coming soon!')}
                  data-testid="upgrade-premium-btn"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade to Premium
                </Button>

                <p className="mt-6 text-sm text-[#737373]">
                  Already premium?{' '}
                  <button 
                    onClick={() => window.location.reload()}
                    className="text-[#00FF94] hover:text-[#00FF94]/80"
                  >
                    Refresh page
                  </button>
                </p>
              </div>

              {/* Blurred preview */}
              <div className="border-t border-[#1F1F1F] p-6 relative overflow-hidden">
                <div className="premium-blur select-none">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-black/50 rounded-md p-3">
                      <p className="text-xs text-[#737373] uppercase tracking-wider mb-1">Entry</p>
                      <p className="font-mono text-white">$00,000.00</p>
                    </div>
                    <div className="bg-black/50 rounded-md p-3">
                      <p className="text-xs text-[#737373] uppercase tracking-wider mb-1">Stop Loss</p>
                      <p className="font-mono text-[#FF0055]">$00,000.00</p>
                    </div>
                    <div className="bg-black/50 rounded-md p-3">
                      <p className="text-xs text-[#737373] uppercase tracking-wider mb-1">Take Profit</p>
                      <p className="font-mono text-[#00FF94]">$00,000.00</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="mt-6 text-center">
            <Button 
              variant="outline" 
              onClick={() => navigate('/signals')}
              className="border-[#1F1F1F] bg-transparent hover:bg-white/5 text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Signals
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  // Full signal view
  return (
    <Layout>
      <div className="max-w-4xl mx-auto animate-fade-in-up">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/signals')}
          className="text-[#A1A1AA] hover:text-white mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Signals
        </Button>

        <Card className="border-[#1F1F1F] bg-[#0A0A0A]">
          <CardHeader className="border-b border-[#1F1F1F]">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  isLong ? 'bg-[#00FF94]/10' : 'bg-[#FF0055]/10'
                }`}>
                  {isLong ? (
                    <TrendingUp className="w-6 h-6 text-[#00FF94]" />
                  ) : (
                    <TrendingDown className="w-6 h-6 text-[#FF0055]" />
                  )}
                </div>
                <div>
                  <CardTitle className="text-2xl font-bold text-white font-mono">{signal.symbol}</CardTitle>
                  <span className={`text-sm font-mono uppercase tracking-wider ${
                    isLong ? 'text-[#00FF94]' : 'text-[#FF0055]'
                  }`}>
                    {direction}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {signalIsPremium ? (
                  <Badge className="bg-[#6366F1]/10 text-[#6366F1] border border-[#6366F1]/20 font-mono text-xs uppercase tracking-wider">
                    <Crown className="w-3 h-3 mr-1" />
                    Premium
                  </Badge>
                ) : (
                  <Badge className="bg-white/5 text-white/60 border border-white/10 font-mono text-xs uppercase tracking-wider">
                    Free
                  </Badge>
                )}
              </div>
            </div>
            {signal.title && (
              <p className="text-[#A1A1AA] mt-2">{signal.title}</p>
            )}
          </CardHeader>

          <CardContent className="p-6 space-y-6">
            {/* Entry Zone */}
            <div className="bg-black/50 border border-[#1F1F1F] rounded-lg p-4">
              <h4 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
                <Target className="w-4 h-4 text-[#00FF94]" />
                Entry Zone
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-md border border-[#1F1F1F]">
                  <span className="text-xs text-[#737373] uppercase tracking-wider">Entry Min</span>
                  <span className="font-mono text-white">{signal.entry_zone_min || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-md border border-[#1F1F1F]">
                  <span className="text-xs text-[#737373] uppercase tracking-wider">Entry Max</span>
                  <span className="font-mono text-white">{signal.entry_zone_max || 'N/A'}</span>
                </div>
              </div>
              {signal.entry_notes && (
                <p className="text-sm text-[#A1A1AA] mt-3">{signal.entry_notes}</p>
              )}
            </div>

            {/* Stop Loss */}
            <div className="bg-black/50 border border-[#1F1F1F] rounded-lg p-4">
              <h4 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-[#FF0055]" />
                Stop Loss Levels
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-md border border-[#1F1F1F]">
                  <span className="text-xs text-[#737373] uppercase tracking-wider">Defensive SL</span>
                  <span className="font-mono text-[#FF0055]">{signal.defensive_sl || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-md border border-[#1F1F1F]">
                  <span className="text-xs text-[#737373] uppercase tracking-wider">Aggressive SL</span>
                  <span className="font-mono text-[#FF0055]">{signal.aggressive_sl || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Take Profit Levels */}
            <div className="bg-black/50 border border-[#1F1F1F] rounded-lg p-4">
              <h4 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
                <Target className="w-4 h-4 text-[#00FF94]" />
                Take Profit Targets
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-md border border-[#1F1F1F]">
                  <span className="text-xs text-[#737373] uppercase tracking-wider">TP 1</span>
                  <span className="font-mono text-[#00FF94]">{signal.tp1 || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-md border border-[#1F1F1F]">
                  <span className="text-xs text-[#737373] uppercase tracking-wider">TP 2</span>
                  <span className="font-mono text-[#00FF94]">{signal.tp2 || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-md border border-[#1F1F1F]">
                  <span className="text-xs text-[#737373] uppercase tracking-wider">TP 3</span>
                  <span className="font-mono text-[#00FF94]">{signal.tp3 || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-black/50 border border-[#1F1F1F] rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <BarChart3 className="w-4 h-4 text-[#A1A1AA]" />
                  <p className="text-xs text-[#737373] uppercase tracking-wider">Risk/Reward</p>
                </div>
                <p className="font-mono text-xl text-white">{signal.risk_reward || 'N/A'}</p>
              </div>
              <div className="bg-black/50 border border-[#1F1F1F] rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-4 h-4 text-[#A1A1AA]" />
                  <p className="text-xs text-[#737373] uppercase tracking-wider">Leverage</p>
                </div>
                <p className="font-mono text-xl text-white">{signal.leverage_recommended ? `${signal.leverage_recommended}x` : 'N/A'}</p>
              </div>
              <div className="bg-black/50 border border-[#1F1F1F] rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-[#A1A1AA]" />
                  <p className="text-xs text-[#737373] uppercase tracking-wider">Created</p>
                </div>
                <p className="font-mono text-sm text-white">
                  {signal.created_at ? new Date(signal.created_at).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  }) : 'N/A'}
                </p>
              </div>
            </div>

            {/* Volatility */}
            {signal.volatility && (
              <div className="bg-black/50 border border-[#1F1F1F] rounded-lg p-4">
                <h4 className="text-sm font-medium text-white mb-2">Volatility</h4>
                <Badge 
                  variant="outline" 
                  className="font-mono text-xs uppercase border-[#737373]/30 text-[#737373]"
                >
                  {signal.volatility}
                </Badge>
              </div>
            )}

            {/* Notes */}
            {signal.notes && (
              <div className="bg-black/50 border border-[#1F1F1F] rounded-lg p-4">
                <h4 className="text-sm font-medium text-white mb-2">Analysis Notes</h4>
                <p className="text-sm text-[#A1A1AA] whitespace-pre-wrap">{signal.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default SignalDetail;
